import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, TextField, Checkbox, Stack, Select } from '@eds-web/react';
import { FormikProps } from 'formik';
import { TaxProfile } from '../../types/payout.types';

interface MailingAddressSectionProps {
    formik: FormikProps<any>;
    taxProfile: TaxProfile;
    isDisabled: boolean;
    isDiffCountry: boolean;
    handleChangeFormValues: (_: string, arg?: any) => void;
    stateList: any;
    defaultState: any;
}

export const MailingAddressSection: React.FC<MailingAddressSectionProps> = ({
    formik,
    taxProfile,
    isDisabled,
    isDiffCountry,
    stateList,
    defaultState,
    handleChangeFormValues
}) => {
    const { t } = useTranslation();
    const useSelect = ['US', 'CA', 'AU'].indexOf(taxProfile.mailingAddressCountryCode) > -1;
    return (
        <Stack gap={24}>
            <div className="form-column">
                <Checkbox
                    disabled={Boolean(!taxProfile.recordIdentityKey || isDiffCountry)}
                    name="mailingAddressDifferent"
                    checked={Boolean(formik.values.mailingAddressDifferent)}
                    onChange={(checked) => {
                        formik.setFieldValue('mailingAddressDifferent', checked);
                    }}
                    label={t('epic.esport.profile.info.mailingAddress')}
                />
            </div>

            {formik.values.mailingAddressDifferent && (
                <>
                    <Text variant="headingSM">{t('epic.esport.profile.title.mailing')}</Text>
                    <div className="form-column">
                        <TextField
                            label={t('epic.esport.label.profile.address1')}
                            name="mailingAddressLine1"
                            value={formik.values.mailingAddressLine1}
                            onChange={handleChangeFormValues}
                            placeholder={t('epic.esport.label.profile.address1')}
                            disabled={isDisabled}
                            error={
                                formik.touched.mailingAddressLine1 &&
                                (formik.errors.mailingAddressLine1 as string)
                            }
                            required
                        />
                        <TextField
                            label={t('epic.esport.label.profile.address2')}
                            name="mailingAddressLine2"
                            value={formik.values.mailingAddressLine2}
                            onChange={handleChangeFormValues}
                            placeholder={t('epic.esport.label.profile.address2')}
                            disabled={isDisabled}
                            error={
                                formik.touched.mailingAddressLine2 &&
                                (formik.errors.mailingAddressLine2 as string)
                            }
                        />
                    </div>
                    <div className="form-column">
                        <TextField
                            label={t('epic.esport.label.payoutInfo.city')}
                            name="mailingAddressCity"
                            value={formik.values.mailingAddressCity}
                            onChange={handleChangeFormValues}
                            placeholder={t('epic.esport.label.payoutInfo.city')}
                            disabled={isDisabled}
                            error={
                                formik.touched.mailingAddressCity &&
                                (formik.errors.mailingAddressCity as string)
                            }
                            required
                        />
                        {useSelect ? (
                            <Select
                                label={t('epic.esport.label.profile.region')}
                                options={stateList as any}
                                defaultValue={defaultState}
                                disabled={isDisabled}
                                placeholder={t('epic.esport.label.profile.region')}
                                onChange={(selected: any) => {
                                    formik.setFieldValue('mailingAddressState', selected?.value);
                                    formik.setFieldTouched('mailingAddressState', true, false);
                                }}
                            />
                        ) : (
                            <TextField
                                label={t('epic.esport.label.profile.region')}
                                name="mailingAddressState"
                                value={formik.values.mailingAddressState}
                                onChange={handleChangeFormValues}
                                placeholder={t('epic.esport.label.profile.region')}
                                disabled={isDisabled}
                                error={
                                    formik.touched.mailingAddressState &&
                                    (formik.errors.mailingAddressState as string)
                                }
                            />
                        )}
                    </div>
                    <div className="form-column">
                        <TextField
                            label={t('epic.esport.label.payoutInfo.country')}
                            value={taxProfile?.mailingAddressCountryCode}
                            placeholder={t('epic.esport.label.payoutInfo.country')}
                            disabled
                        />
                        <TextField
                            label={t('epic.esport.label.profile.postalCode')}
                            name="mailingAddressPostCode"
                            value={formik.values.mailingAddressPostCode}
                            onChange={handleChangeFormValues}
                            placeholder={t('epic.esport.label.profile.postalCode')}
                            disabled={isDisabled}
                            error={
                                formik.touched.mailingAddressPostCode &&
                                (formik.errors.mailingAddressPostCode as string)
                            }
                        />
                    </div>
                </>
            )}
        </Stack>
    );
};
